/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2022
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
/* eslint-disable react/prop-types */

import {
  ComposedModal,
  ModalHeader,
  ModalBody,
  ModalFooter
} from 'carbon-components-react';
import React from 'react';
import ReactDOM from 'react-dom';
import { FormattedMessage, useIntl } from 'react-intl';

import { Noop } from '../../../../../ts';
import { useHeader } from '../../../hooks/useHeader';
import { messages } from './messages';

import './InactivityModal.scss';

export interface InactivityModalProps {
  isOpen: boolean;
  onClose: Noop;
  minutesRemaining: string;
  isExpired: boolean;
}
export const InactivityModal = React.memo<InactivityModalProps>(
  ({ isOpen, minutesRemaining, isExpired, onClose }) => {
    const intl = useIntl();
    const { onLogout } = useHeader();

    const onCloseCallback = isExpired ? onLogout : onClose;

    return ReactDOM.createPortal(
      <ComposedModal
        data-testid="inactivity-modal"
        className="InactivityModal"
        open={isOpen}
        onClose={onCloseCallback}
        size="sm">
        <ModalHeader>
          <div className="InactivityModal__title">
            <FormattedMessage {...messages.header} />
          </div>
        </ModalHeader>
        {isExpired ? (
          <>
            <ModalBody>
              <FormattedMessage {...messages.expired} />
            </ModalBody>
            <ModalFooter
              primaryButtonText={intl.formatMessage(messages.logOut)}
              onRequestSubmit={onCloseCallback}
            />
          </>
        ) : (
          <>
            <ModalBody>
              <FormattedMessage
                {...messages.aboutToExpire}
                values={{ minutesRemaining }}
              />
            </ModalBody>
            <ModalFooter
              primaryButtonText={intl.formatMessage(messages.continueSession)}
              onRequestSubmit={onCloseCallback}
            />
          </>
        )}
      </ComposedModal>,
      document.body
    );
  }
);

InactivityModal.displayName = 'InactivityModal';
